import i18n from 'i18next';
import { FC, PropsWithChildren } from 'react';
import { StudyNotesReportOptions } from './studyNotesReport.options';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { RPDF } from '../react-pdf';
import { FirstPage } from '../monitoringForms/monitoringFormsReport.layout';
import { useForm } from 'react-hook-form';
import { DataType } from 'src/components/formEditor/uiEditor/uiEditor';
import { IStudyNoteConfigDto, IStudyNoteResultDto } from 'src/services/api/api-client';
import { GeneralFormAnswer } from 'src/features/generalForms/answerPreview/generalFormAnswer';
import { StudyNotesReportControlsResolver } from './studyNotesReportControlsResolver';
import { DateFormats, localFormat } from 'src/helpers/date-helpers';
import { FormHeader } from '../records/components/FormHeader';
import { ReportHeader } from '../components/ReportHeader';
import { ReportColors } from '../report-colors';
import { ReportPagination } from '../components/ReportPagination';

export const StudyNotesReportLayout: FC<StudyNotesReportOptions> = (props) => {
  const { t } = useScopedTranslation('Reports.StudyNotesReport');

  return (
    <RPDF.Document
      language={i18n.language}
      producer={'LM Study'}
      title={t('DocumentTitle')}
      author={props.authorFullName}
      subject={props.studyNumber}
    >
      <FirstPage
        title={t('DocumentTitle')}
        formsTotal={props.resultsWithConfig.length}
        studyName={props.studyName}
        studyNumber={props.studyNumber}
        authorFullName={props.authorFullName}
      />
      {props.resultsWithConfig.map(({ result, config }) => (
        <StudyNotesPageWrapper
          key={result.formResultId}
          title={config.title}
          subTitleText={`${props.studyName} (${props.studyNumber})`}
          formType={config.title}
          created={{
            date: result.createdAt,
            fullName: result.createdBy.fullName,
          }}
          updated={{
            date: result.updatedAt,
            fullName: result.updatedBy.fullName,
          }}
        >
          <StudyNotesPdfView config={config} result={result} />
        </StudyNotesPageWrapper>
      ))}
    </RPDF.Document>
  );
};

const StudyNotesPdfView: FC<{ config: IStudyNoteConfigDto; result: IStudyNoteResultDto }> = ({ config, result }) => {
  const form = useForm<DataType>({
    defaultValues: result.value as DataType,
  });

  return (
    <GeneralFormAnswer
      form={form}
      resolver={StudyNotesReportControlsResolver}
      defaultLayout={config.layout}
      initialScoreSections={config.scoreSections ?? []}
      initialVariables={config.variables ?? []}
    />
  );
};

export const StudyNotesPageWrapper: FC<
  PropsWithChildren<{
    title: string;
    subTitleText?: string;
    formType: string;
    created: {
      date: Date;
      fullName: string;
    };
    updated: {
      date: Date;
      fullName: string;
    };
  }>
> = ({ title, subTitleText, children, created, updated, formType }) => {
  const { t } = useScopedTranslation('Reports');

  const reportWasEdited = created.date.getTime() !== updated.date.getTime();
  const lastEventText = reportWasEdited
    ? t('Records.Updated', {
        date: localFormat(updated.date, DateFormats.longDate),
        name: updated.fullName,
      })
    : t('Records.Filled', {
        date: localFormat(created.date, DateFormats.longDate),
        name: created.fullName,
      });

  const gap = ' ';

  return (
    <RPDF.Page size={'A4'} style={RPDF.styles.page}>
      <RPDF.View style={{ flex: 1, flexDirection: 'column' }}>
        <ReportHeader titleText={title} subTitleText={subTitleText} />
        <FormHeader
          title={localFormat(created.date, DateFormats.longDate)}
          formState={<RPDF.Text>{lastEventText}</RPDF.Text>}
        />
        <RPDF.Text
          fixed={true}
          style={{ marginBottom: 12, color: ReportColors.plain }}
          render={({ subPageNumber }) =>
            subPageNumber > 1 && (
              <>
                <RPDF.Text style={RPDF.typography.heading4}>{formType}</RPDF.Text>
                {gap}
                <RPDF.Text style={RPDF.typography.text10}>{lastEventText}</RPDF.Text>
              </>
            )
          }
        />
        {children}
        <ReportPagination />
      </RPDF.View>
    </RPDF.Page>
  );
};
